import {
  type ConnectionOperation,
  DOCUMENT_FIELDS_PATH,
  type EntityOperation,
  type SetFieldsOperation,
} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {useMutation} from '@github-ui/react-query'
import {useCallback} from 'react'
import {
  ConnectionHandler,
  type Disposable,
  type Environment,
  type RecordProxy,
  type RecordSourceProxy,
} from 'relay-runtime'

import {
  addCommentOperation,
  deleteCommentOperation,
  type OptimisticConnection,
  updateCommentOperation,
} from './comment-connection-overlay'
import {
  addCommentRequest,
  type CommentRoute,
  deleteIssueCommentRequest,
  type ExistingCommentRoute,
  updateIssueCommentBodyRequest,
} from './comment-mutation-requests'

type MutationCallbacks = {
  onCompleted?: () => void
  onError?: (error: Error) => void
}

type AddCommentResponse = Awaited<ReturnType<typeof addCommentRequest>>
type DeleteCommentResponse = Awaited<ReturnType<typeof deleteIssueCommentRequest>>

type AddMetadata = MutationCallbacks & {
  kind: 'add'
  route: CommentRoute
  body: string
  execute: () => Promise<AddCommentResponse>
}
type DeleteMetadata = MutationCallbacks & {
  kind: 'delete'
  route: ExistingCommentRoute
  nodeId: string
  execute: () => Promise<DeleteCommentResponse>
}
type UpdateMetadata = MutationCallbacks & {
  kind: 'update'
  route: ExistingCommentRoute
  body: string
  bodyVersion: string
}
type CommentMutationMetadata = AddMetadata | DeleteMetadata | UpdateMetadata
type CommentMutationOperation<
  TOperation extends ConnectionOperation = ConnectionOperation,
  TMetadata extends CommentMutationMetadata = CommentMutationMetadata,
> = TOperation & {commentMutation: TMetadata}
type CountMutationVariables = {operation: EntityOperation; operationId: string}

function withMetadata<TOperation extends ConnectionOperation, TMetadata extends CommentMutationMetadata>(
  operation: TOperation,
  commentMutation: TMetadata,
): CommentMutationOperation<TOperation, TMetadata> {
  return {...operation, commentMutation}
}

function metadataFor<TKind extends CommentMutationMetadata['kind']>(
  operation: ConnectionOperation,
  kind: TKind,
): Extract<CommentMutationMetadata, {kind: TKind}> {
  const metadata = (operation as Partial<CommentMutationOperation>).commentMutation
  if (!metadata || metadata.kind !== kind) throw new Error(`Missing ${kind} comment mutation input`)
  return metadata as Extract<CommentMutationMetadata, {kind: TKind}>
}

function asError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback)
}

function asCommentNode(node: object): Record<string, unknown> & {id: string} {
  return node as Record<string, unknown> & {id: string}
}

const RELAY_TIMELINE_CONNECTION_KEYS = ['Issue__frontTimelineItems', 'Issue__backTimelineItems'] as const
const RELAY_TIMELINE_CONNECTION_FILTERS = {visibleEventsOnly: true}

function relayTypeForField(field: string): string {
  switch (field) {
    case 'issue':
      return 'Issue'
    case 'repository':
      return 'Repository'
    case 'viaApp':
      return 'App'
    case 'authorToRepoOwnerSponsorship':
      return 'Sponsorship'
    case 'lastUserContentEdit':
      return 'UserContentEdit'
    case 'reactionGroups':
      return 'ReactionGroup'
    case 'reactors':
      return 'ReactorConnection'
    default:
      return 'User'
  }
}

function isRelayObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function writeRelayObject(
  store: RecordSourceProxy,
  data: Record<string, unknown>,
  recordId: string,
  fallbackType: string,
): RecordProxy {
  const typeName = typeof data.__typename === 'string' ? data.__typename : fallbackType
  const record = store.get(recordId) ?? store.create(recordId, typeName)

  for (const [field, value] of Object.entries(data)) {
    if (field === '__typename' || value === undefined) continue
    if (Array.isArray(value)) {
      record.setLinkedRecords(
        value.flatMap((item, index) => {
          if (!isRelayObject(item)) return []
          const childId = typeof item.id === 'string' ? item.id : `client:${recordId}:${field}:${index}`
          return [writeRelayObject(store, item, childId, relayTypeForField(field))]
        }),
        field,
      )
    } else if (isRelayObject(value)) {
      const childId = typeof value.id === 'string' ? value.id : `client:${recordId}:${field}`
      record.setLinkedRecord(writeRelayObject(store, value, childId, relayTypeForField(field)), field)
    } else {
      record.setValue(value, field)
      if (field === 'bodyHTML') record.setValue(value, field, {unfurlReferences: true})
    }
  }

  return record
}

function relayTimelineConnection(
  store: RecordSourceProxy,
  issueId: string,
  key: (typeof RELAY_TIMELINE_CONNECTION_KEYS)[number],
) {
  return store.get(ConnectionHandler.getConnectionID(issueId, key, RELAY_TIMELINE_CONNECTION_FILTERS))
}

function addDelta(record: RecordProxy, field: string, delta: number) {
  const current = record.getValue(field)
  if (typeof current === 'number') record.setValue(Math.max(0, current + delta), field)
}

function writeRelayAddedComment(
  store: RecordSourceProxy,
  issueId: string,
  node: (Record<string, unknown> & {id?: string | null}) | null | undefined,
  totalCommentsCount?: number,
) {
  const issue = store.get(issueId)
  if (!issue) return

  if (totalCommentsCount === undefined) addDelta(issue, 'totalCommentsCount', 1)
  else issue.setValue(totalCommentsCount, 'totalCommentsCount')

  for (const key of RELAY_TIMELINE_CONNECTION_KEYS) {
    const connection = relayTimelineConnection(store, issueId, key)
    if (connection) addDelta(connection, 'totalCount', 1)
  }

  if (!node || typeof node.id !== 'string') return
  const connection = relayTimelineConnection(store, issueId, 'Issue__backTimelineItems')
  if (!connection) return
  const existingNodeIds = new Set(
    (connection.getLinkedRecords('edges') ?? []).map(edge => edge?.getLinkedRecord('node')?.getDataID()),
  )
  if (existingNodeIds.has(node.id)) return

  const comment = writeRelayObject(store, node, node.id, 'IssueComment')
  const edge = ConnectionHandler.createEdge(store, connection, comment, 'IssueTimelineItemsEdge')
  ConnectionHandler.insertEdgeAfter(connection, edge)
}

function writeRelayDeletedComment(
  store: RecordSourceProxy,
  issueId: string,
  commentId: string,
  totalCommentsCount?: number,
) {
  const issue = store.get(issueId)
  if (!issue) return

  if (totalCommentsCount === undefined) addDelta(issue, 'totalCommentsCount', -1)
  else issue.setValue(totalCommentsCount, 'totalCommentsCount')

  for (const key of RELAY_TIMELINE_CONNECTION_KEYS) {
    const connection = relayTimelineConnection(store, issueId, key)
    if (!connection) continue
    ConnectionHandler.deleteNode(connection, commentId)
    addDelta(connection, 'totalCount', -1)
  }
}

export function useAddCommentMutation({
  connection: {ownerId, connectionPath},
  environment,
}: {
  connection: OptimisticConnection
  environment: Environment
}) {
  const mutation = useConnectionMutation<AddCommentResponse, Error, Disposable>({
    ownerId,
    connectionPath,
    mutationFn: operation => metadataFor(operation, 'add').execute(),
    onMutate: ({operation}) =>
      environment.applyUpdate({
        storeUpdater: store =>
          writeRelayAddedComment(store, ownerId, operation.type === 'addToConnection' ? operation.node : undefined),
      }),
    onSuccess: async (data, variables, relayUpdate) => {
      const metadata = metadataFor(variables.operation, 'add')
      const node = data.addComment?.timelineEdge?.node
      if (!node) throw new Error('Add comment mutation returned no comment')

      const temporaryId = variables.operation.type === 'addToConnection' ? variables.operation.node.id : undefined
      const confirmed = addCommentOperation({
        connectionPath,
        node: asCommentNode(node),
        temporaryId,
        fallbackId: ownerId,
      })
      const subject = data.addComment?.subject
      const totalCommentsCount =
        subject && 'totalCommentsCount' in subject && subject.totalCommentsCount != null
          ? subject.totalCommentsCount
          : undefined
      environment.commitUpdate(store => writeRelayAddedComment(store, ownerId, asCommentNode(node), totalCommentsCount))
      relayUpdate.dispose()
      await notifySettlement(confirmed, ownerId, connectionPath)

      metadata.onCompleted?.()
    },
    onError: (error, variables, relayUpdate) => {
      relayUpdate?.dispose()
      const metadata = metadataFor(variables.operation, 'add')
      metadata.onError?.(error)
    },
  })
  const {mutate: mutateCount} = useMutation<AddCommentResponse, Error, CountMutationVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: ({operation}) => metadataFor(operation, 'add').execute(),
    onSuccess: async (_data, variables) => {
      await notifySettlement(variables.operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, variables.operationId)
    },
  })

  return useCallback(
    ({
      route,
      body,
      optimisticNode,
      onCompleted,
      onError,
    }: MutationCallbacks & {
      route: CommentRoute
      body: string
      optimisticNode?: (Record<string, unknown> & {id?: string | null}) | null
    }) => {
      let request: Promise<AddCommentResponse> | undefined
      const execute = () => (request ??= addCommentRequest(route, {body}))
      const metadata: AddMetadata = {kind: 'add', route, body, execute, onCompleted, onError}
      const operation = withMetadata(
        addCommentOperation({
          connectionPath,
          node: optimisticNode,
          temporaryId: optimisticNode?.id ?? undefined,
          fallbackId: ownerId,
        }),
        metadata,
      )
      const countOperation = withMetadata<EntityOperation, AddMetadata>(
        {type: 'deltaEntityField', id: ownerId, field: 'comments', delta: 1, clamp: {min: 0}},
        metadata,
      )

      try {
        mutation.mutate({operation})
        mutateCount({operation: countOperation, operationId: crypto.randomUUID()})
      } catch (error) {
        const mutationError = asError(error, 'Failed to add comment')
        onError?.(mutationError)
      }
    },
    [connectionPath, mutateCount, mutation, ownerId],
  )
}

export function useDeleteIssueCommentMutation({
  connection: {ownerId, connectionPath},
  environment,
}: {
  connection: OptimisticConnection
  environment: Environment
}) {
  const mutation = useConnectionMutation<DeleteCommentResponse, Error, Disposable>({
    ownerId,
    connectionPath,
    mutationFn: operation => metadataFor(operation, 'delete').execute(),
    onMutate: ({operation}) => {
      const commentId = metadataFor(operation, 'delete').nodeId
      return environment.applyUpdate({
        storeUpdater: store => writeRelayDeletedComment(store, ownerId, commentId),
      })
    },
    onSuccess: async (data, variables, relayUpdate) => {
      const metadata = metadataFor(variables.operation, 'delete')
      const issue = data.deleteIssueComment?.issueComment?.issue
      const commentId = data.deleteIssueComment?.issueComment?.id
      if (!commentId) throw new Error('Delete comment mutation returned no comment')
      environment.commitUpdate(store =>
        writeRelayDeletedComment(store, ownerId, commentId, issue?.totalCommentsCount ?? undefined),
      )
      relayUpdate.dispose()
      await notifySettlement(variables.operation, ownerId, connectionPath)

      metadata.onCompleted?.()
    },
    onError: (error, variables, relayUpdate) => {
      relayUpdate?.dispose()
      const metadata = metadataFor(variables.operation, 'delete')
      metadata.onError?.(error)
    },
  })
  const {mutate: mutateCount} = useMutation<DeleteCommentResponse, Error, CountMutationVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: ({operation}) => metadataFor(operation, 'delete').execute(),
    onSuccess: async (_data, variables) => {
      await notifySettlement(variables.operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, variables.operationId)
    },
  })

  return useCallback(
    ({route, nodeId, onCompleted, onError}: MutationCallbacks & {route: ExistingCommentRoute; nodeId: string}) => {
      let request: Promise<DeleteCommentResponse> | undefined
      const execute = () => (request ??= deleteIssueCommentRequest(route))
      const metadata: DeleteMetadata = {
        kind: 'delete',
        route,
        nodeId,
        execute,
        onCompleted,
        onError,
      }
      const operation = withMetadata(deleteCommentOperation(connectionPath, nodeId), metadata)
      const countOperation = withMetadata<EntityOperation, DeleteMetadata>(
        {type: 'deltaEntityField', id: ownerId, field: 'comments', delta: -1, clamp: {min: 0}},
        metadata,
      )

      try {
        mutation.mutate({operation})
        mutateCount({operation: countOperation, operationId: crypto.randomUUID()})
      } catch (error) {
        const mutationError = asError(error, 'Failed to delete comment')
        onError?.(mutationError)
      }
    },
    [connectionPath, mutateCount, mutation, ownerId],
  )
}

export type UpdateIssueCommentBodyMutation = (
  input: MutationCallbacks & {
    route: ExistingCommentRoute
    nodeId: string
    body: string
    bodyVersion: string
  },
) => void

type UpdateIssueCommentBodyResponse = Awaited<ReturnType<typeof updateIssueCommentBodyRequest>>

function confirmedUpdateCommentOperation(data: UpdateIssueCommentBodyResponse): SetFieldsOperation {
  const comment = data.updateIssueComment?.issueComment
  if (!comment) throw new Error('Update comment mutation returned no comment')
  return {
    type: 'setFields',
    id: comment.id,
    fields: {
      body: comment.body,
      bodyHTML: comment.bodyHTML,
      bodyVersion: comment.bodyVersion,
      viewerCanUpdate: comment.viewerCanUpdate,
    },
  }
}

function writeRelayCommentFields(store: RecordSourceProxy, operation: SetFieldsOperation) {
  const record = store.get(operation.id)
  if (!record) return
  for (const [field, value] of Object.entries(operation.fields)) record.setValue(value, field)
}

export function useUpdateIssueCommentBodyMutation({
  environment,
}: {
  environment: Environment
}): UpdateIssueCommentBodyMutation {
  const mutation = useEntityMutation<UpdateIssueCommentBodyResponse, Error, Disposable>({
    mutationFn: operation => {
      const {route, body, bodyVersion} = metadataFor(operation, 'update')
      return updateIssueCommentBodyRequest(route, {body, bodyVersion})
    },
    onMutate: ({operation}) =>
      environment.applyUpdate({
        storeUpdater: store => writeRelayCommentFields(store, operation),
      }),
    getConfirmedOperation: confirmedUpdateCommentOperation,
    onSuccess: async (data, variables, relayUpdate) => {
      const metadata = metadataFor(variables.operation, 'update')
      const confirmed = confirmedUpdateCommentOperation(data)

      environment.commitUpdate(store => writeRelayCommentFields(store, confirmed))
      relayUpdate.dispose()

      metadata.onCompleted?.()
    },
    onError: (error, variables, relayUpdate) => {
      relayUpdate?.dispose()
      metadataFor(variables.operation, 'update').onError?.(error)
    },
  })

  return useCallback(
    ({
      route,
      nodeId,
      body,
      bodyVersion,
      onCompleted,
      onError,
    }: MutationCallbacks & {
      route: ExistingCommentRoute
      nodeId: string
      body: string
      bodyVersion: string
    }) => {
      const operation = withMetadata(updateCommentOperation(nodeId, body, bodyVersion), {
        kind: 'update',
        route,
        body,
        bodyVersion,
        onCompleted,
        onError,
      })
      try {
        mutation.mutate({operation})
      } catch (error) {
        onError?.(asError(error, 'Failed to update comment'))
      }
    },
    [mutation],
  )
}
